import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { KeyRound, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Forced password-change dialog mounted at the App root. Auto-opens when
 * the logged-in user has `must_change_password=true` (i.e. they just logged
 * in with a temporary password issued via the forgot-password flow).
 *
 * Cannot be dismissed — the only escape is to submit a valid new password
 * OR log out.
 */
export default function ChangePasswordDialog() {
  const { t } = useTranslation();
  const { user, refresh, logout } = useAuth();
  const open = !!(user && user.must_change_password);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (next.length < 8) {
      setError(t("change_pwd.err_short"));
      return;
    }
    if (next !== confirm) {
      setError(t("change_pwd.err_mismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(t("change_pwd.toast_success"));
      // Refresh user so must_change_password becomes false and the dialog hides.
      await refresh?.();
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e2) {
      setError(
        formatApiErrorDetail(e2.response?.data?.detail) || t("change_pwd.err_generic"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="bg-zinc-950 border-orange-500/40 max-w-md [&>button[aria-label=Close]]:hidden"
        // Block close-on-outside-click + close-on-escape — user must submit.
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="change-password-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-outfit flex items-center gap-2">
            <KeyRound className="text-orange-400" size={18} />
            {t("change_pwd.title")}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {t("change_pwd.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
              {t("change_pwd.current_label")}
            </Label>
            <Input
              type="password"
              required
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="bg-zinc-900/50 border-white/10 mt-2 h-11"
              data-testid="change-pwd-current"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
              {t("change_pwd.new_label")}
            </Label>
            <Input
              type="password"
              required
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="bg-zinc-900/50 border-white/10 mt-2 h-11"
              data-testid="change-pwd-new"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
              {t("change_pwd.confirm_label")}
            </Label>
            <Input
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-zinc-900/50 border-white/10 mt-2 h-11"
              data-testid="change-pwd-confirm"
            />
          </div>

          {error && (
            <div
              className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 px-3 py-2 rounded-md flex items-start gap-2"
              data-testid="change-pwd-error"
            >
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={async () => {
                await logout?.();
                window.location.href = "/login";
              }}
              className="text-zinc-400 hover:text-white"
              data-testid="change-pwd-logout"
            >
              {t("change_pwd.logout_btn")}
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="ml-auto bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="change-pwd-submit"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  {t("change_pwd.submit_loading")}
                </>
              ) : (
                t("change_pwd.submit_btn")
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
